import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO component. Pass in page-specific title, description, and
 * canonical slug. Everything else is inherited from index.html.
 *
 * Usage:
 *   <SEO
 *     title="URLs for Sale"
 *     description="Curated portfolio of premium domain names..."
 *     path="/urlsforsale"
 *   />
 */
const SITE_NAME = 'Shire Advisors, LLC';
const SITE_URL = 'https://shireadvisors.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo-key.png`;

const SEO = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* Optional structured data for this page */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
